/**
 * RendPleX Next Move™ — Premium portfolio-level intelligence card.
 *
 * Renders up to 3 prioritized recommendations returned by
 * `GET /api/next-move`. Free/Pro users see a locked preview instead of
 * personalized data — no leak of Premium content until entitlement is
 * confirmed by the backend (403 → we render <Locked/>).
 *
 * We deliberately keep this component slim: all financial calculations
 * happen server-side (deterministic Next Move engine on top of
 * Portfolio Doctor). The frontend only maps `title_key`/`values` to
 * i18n and wires CTAs into existing RendPleX routes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Sparkles, ChevronDown, ChevronUp, ArrowUpRight, Lock, RefreshCw,
  AlertTriangle, TrendingUp, Landmark, Compass, ShieldCheck,
} from 'lucide-react';
import { Button } from '../ui/button';
import { fireChatAnalytics } from '../../lib/chatAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ANALYTICS_KEY = '__next_move__';

const CATEGORY_ICONS = {
  risk: AlertTriangle,
  cashflow: TrendingUp,
  refinance: Landmark,
  acquisition: Compass,
  hold: ShieldCheck,
};

const CATEGORY_ACCENT = {
  risk: 'text-red-500 bg-red-500/10 ring-red-500/20',
  cashflow: 'text-amber-500 bg-amber-500/10 ring-amber-500/20',
  refinance: 'text-blue-500 bg-blue-500/10 ring-blue-500/20',
  acquisition: 'text-[#45C04A] bg-[#45C04A]/10 ring-[#45C04A]/20',
  hold: 'text-emerald-500 bg-emerald-500/10 ring-emerald-500/20',
};

const CONFIDENCE_META = {
  high: { dot: 'bg-emerald-500', label: 'nextMove.confidence.high' },
  moderate: { dot: 'bg-amber-500', label: 'nextMove.confidence.moderate' },
  limited: { dot: 'bg-slate-300', label: 'nextMove.confidence.limited' },
};

export default function NextMoveCard({ isPremium }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [failed, setFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [openWhy, setOpenWhy] = useState({});

  const load = async () => {
    setLoading(true);
    setFailed(false);
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/next-move`, { withCredentials: true });
      setData(resp.data);
      setLocked(false);
      fireChatAnalytics('next_move_viewed', ANALYTICS_KEY, {
        rec_count: (resp.data?.recommendations || []).length,
      });
    } catch (e) {
      if (e.response?.status === 403) {
        setLocked(true);
      } else if (e.response?.status !== 401) {
        setFailed(true);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // Skip fetching on locked (non-Premium) — show upsell instead
    if (isPremium === false) {
      setLocked(true);
      setLoading(false);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremium]);

  const handleRefresh = () => {
    setRefreshing(true);
    fireChatAnalytics('next_move_refresh', ANALYTICS_KEY, {});
    load();
  };

  if (loading && !data) {
    return <Skeleton t={t} />;
  }

  if (locked) {
    return <Locked t={t} navigate={navigate} />;
  }

  if (failed) {
    return <Failed t={t} onRetry={load} />;
  }

  const recs = data?.recommendations || [];
  if (!recs.length) return null;

  const [first, ...others] = recs;

  return (
    <section
      data-testid="next-move-card"
      className="relative overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] shadow-card"
    >
      {/* subtle brand accent gradient */}
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gradient-to-br from-[#45C04A]/10 to-[#12355B]/10 blur-3xl pointer-events-none" />

      <header className="relative px-6 pt-6 pb-4 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#12355B] to-[#1565C0] flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 data-testid="next-move-title" className="font-primary font-extrabold text-xl md:text-2xl text-[#12355B] dark:text-white tracking-tight">
                {t('nextMove.title')}
              </h2>
              <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-[#12355B] text-white dark:bg-[#45C04A] dark:text-[#12355B]">
                {t('nextMove.premiumTag')}
              </span>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {t('nextMove.subtitle')}
            </p>
          </div>
        </div>
        <button
          data-testid="next-move-refresh"
          onClick={handleRefresh}
          className="w-9 h-9 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-500 hover:text-[#12355B] dark:hover:text-white hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
          aria-label={t('nextMove.refresh')}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </header>

      <div className="relative px-6 pb-6 space-y-4">
        <PrimaryRecommendation
          rec={first}
          t={t}
          navigate={navigate}
          open={openWhy[first.id] || false}
          onToggleWhy={() => {
            setOpenWhy((s) => ({ ...s, [first.id]: !s[first.id] }));
            if (!openWhy[first.id]) fireChatAnalytics('next_move_why_opened', ANALYTICS_KEY, { id: first.id, category: first.category });
          }}
        />

        {others.length > 0 && (
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-400 mb-2 mt-6">
              {t('nextMove.otherOpportunities')}
            </p>
            <div className="space-y-2">
              {others.map((r) => (
                <SecondaryRow
                  key={r.id}
                  rec={r}
                  t={t}
                  navigate={navigate}
                  open={openWhy[r.id] || false}
                  onToggleWhy={() => {
                    setOpenWhy((s) => ({ ...s, [r.id]: !s[r.id] }));
                    if (!openWhy[r.id]) fireChatAnalytics('next_move_why_opened', ANALYTICS_KEY, { id: r.id, category: r.category });
                  }}
                />
              ))}
            </div>
          </div>
        )}

        <p data-testid="next-move-disclaimer" className="text-[11px] leading-relaxed text-slate-400 dark:text-slate-500 pt-2 border-t border-slate-100 dark:border-slate-800/60">
          {t('nextMove.disclaimer')}
        </p>
      </div>
    </section>
  );
}

/* -------------------- pieces -------------------- */

function PrimaryRecommendation({ rec, t, navigate, open, onToggleWhy }) {
  const Icon = CATEGORY_ICONS[rec.category] || Sparkles;
  const accent = CATEGORY_ACCENT[rec.category] || CATEGORY_ACCENT.hold;
  const confidence = CONFIDENCE_META[rec.confidence] || CONFIDENCE_META.limited;

  const onCta = () => {
    fireChatAnalytics('next_move_cta_clicked', '__next_move__', {
      id: rec.id, category: rec.category, route: rec.cta.route,
    });
    navigate(rec.cta.route);
  };

  return (
    <div
      data-testid={`next-move-primary-${rec.category}`}
      className="rounded-2xl bg-gradient-to-br from-slate-50 to-white dark:from-slate-800/40 dark:to-[#131824] border border-slate-200 dark:border-slate-800 p-5"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] font-bold tracking-widest uppercase text-[#12355B] dark:text-[#45C04A] bg-[#12355B]/5 dark:bg-[#45C04A]/10 px-2 py-1 rounded-full">
          #1 · {t(rec.rank_label_key)}
        </span>
        <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-1 rounded-full ring-1 ${accent}`}>
          <Icon className="w-3 h-3" /> {t(`nextMove.categoryLabel.${rec.category}`)}
        </span>
      </div>

      <h3 className="font-primary font-extrabold text-lg md:text-xl text-[#12355B] dark:text-white leading-snug">
        {t(rec.title_key, rec.title_values)}
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
        {t(rec.subtitle_key, rec.subtitle_values)}
      </p>

      {rec.property?.address && (
        <p className="text-xs text-slate-400 mt-2">
          {t('nextMove.propertyLabel')}: <span className="font-semibold text-slate-600 dark:text-slate-300">{rec.property.address}</span>
        </p>
      )}

      <div className="mt-4 rounded-xl bg-white dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800 p-3">
        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{t('nextMove.objectiveLabel')}</p>
        <p className="text-sm font-semibold text-[#12355B] dark:text-white">
          {t(rec.objective_key, rec.objective_values)}
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${confidence.dot}`} />
          <span
            data-testid={`next-move-confidence-${rec.id}`}
            className="text-xs text-slate-500 dark:text-slate-400"
            title={t('nextMove.confidence.tooltip')}
          >
            {t('nextMove.confidence.label')}: <span className="font-semibold">{t(confidence.label)}</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={`next-move-why-${rec.id}`}
            onClick={onToggleWhy}
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#12355B] dark:hover:text-white"
          >
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {t('nextMove.whyThis')}
          </button>
          <Button
            data-testid={`next-move-cta-${rec.id}`}
            onClick={onCta}
            className="bg-[#12355B] hover:bg-[#1565C0] text-white rounded-xl px-4 py-2 h-9 text-sm font-semibold"
          >
            {t(rec.cta.label_key)}
            <ArrowUpRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </div>

      {open && rec.why_bullets?.length > 0 && (
        <div data-testid={`next-move-why-panel-${rec.id}`} className="mt-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 p-3">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">
            {t('nextMove.whyIdentified')}
          </p>
          <ul className="space-y-1.5">
            {rec.why_bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-slate-300 flex-shrink-0" />
                <span>{t(b.key, b.values || {})}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SecondaryRow({ rec, t, navigate, open, onToggleWhy }) {
  const Icon = CATEGORY_ICONS[rec.category] || Sparkles;
  const confidence = CONFIDENCE_META[rec.confidence] || CONFIDENCE_META.limited;
  const onCta = () => {
    fireChatAnalytics('next_move_cta_clicked', '__next_move__', {
      id: rec.id, category: rec.category, route: rec.cta.route,
    });
    navigate(rec.cta.route);
  };
  return (
    <div
      data-testid={`next-move-secondary-${rec.category}`}
      className="rounded-xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/30 p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-slate-500" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[9px] uppercase tracking-widest font-bold text-slate-400">#{rec.rank} · {t(rec.rank_label_key)}</span>
              <span className={`w-1.5 h-1.5 rounded-full ${confidence.dot}`} title={t('nextMove.confidence.tooltip')} />
            </div>
            <p className="text-sm font-semibold text-[#12355B] dark:text-white leading-snug truncate">
              {t(rec.title_key, rec.title_values)}
            </p>
            {rec.property?.address && (
              <p className="text-[11px] text-slate-400 truncate">{rec.property.address}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            data-testid={`next-move-why-${rec.id}`}
            onClick={onToggleWhy}
            className="w-8 h-8 rounded-lg text-slate-400 hover:text-[#12355B] dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center"
            aria-label={t('nextMove.whyThis')}
          >
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          <Button
            data-testid={`next-move-cta-${rec.id}`}
            onClick={onCta}
            size="sm"
            variant="outline"
            className="h-8 px-3 rounded-lg text-xs font-semibold"
          >
            {t(rec.cta.label_key)}
          </Button>
        </div>
      </div>
      {open && rec.why_bullets?.length > 0 && (
        <ul data-testid={`next-move-why-panel-${rec.id}`} className="mt-3 space-y-1.5 pl-11">
          {rec.why_bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-slate-300 flex-shrink-0" />
              <span>{t(b.key, b.values || {})}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Locked({ t, navigate }) {
  const handleUpgrade = () => {
    fireChatAnalytics('next_move_upgrade_clicked', '__next_move__', {});
    navigate('/pricing');
  };
  return (
    <section
      data-testid="next-move-locked"
      className="relative overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-gradient-to-br from-[#12355B] to-[#1a4574] p-8 shadow-card"
    >
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#45C04A]/10 blur-3xl pointer-events-none" />
      <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6 text-white">
        <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
          <Lock className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="font-primary font-extrabold text-xl md:text-2xl tracking-tight">
              {t('nextMove.title')}
            </h2>
            <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-[#45C04A] text-[#12355B]">
              {t('nextMove.premiumTag')}
            </span>
          </div>
          <p className="text-white/70 text-sm max-w-xl">
            {t('nextMove.lockedSubtitle')}
          </p>
        </div>
        <Button
          data-testid="next-move-upgrade-cta"
          onClick={handleUpgrade}
          className="bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] rounded-xl px-5 py-2.5 font-bold whitespace-nowrap"
        >
          {t('nextMove.unlockCta')}
        </Button>
      </div>
    </section>
  );
}

function Skeleton({ t }) {
  return (
    <section data-testid="next-move-skeleton" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] p-6 shadow-card animate-pulse">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-2xl bg-slate-100 dark:bg-slate-800" />
        <div className="space-y-2">
          <div className="h-4 w-40 bg-slate-100 dark:bg-slate-800 rounded" />
          <div className="h-3 w-64 bg-slate-100 dark:bg-slate-800 rounded" />
        </div>
      </div>
      <div className="h-32 bg-slate-50 dark:bg-slate-800/50 rounded-xl" />
    </section>
  );
}

function Failed({ t, onRetry }) {
  return (
    <section data-testid="next-move-failed" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] p-6 shadow-card">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="font-semibold text-[#12355B] dark:text-white">{t('nextMove.failed.title')}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('nextMove.failed.subtitle')}</p>
        </div>
        <Button data-testid="next-move-retry" onClick={onRetry} variant="outline" size="sm" className="rounded-xl">
          {t('nextMove.failed.retry')}
        </Button>
      </div>
    </section>
  );
}
